import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { AuthContext } from "../../context/authcontext";
import EmailService, { EmailMessage, SendEmailPayload, PaginatedEmailResponse, BulkMailOperationPayload } from "../../feature-module/application/components/email/email.service";
import { all_routes } from "../../feature-module/router/all_routes";
import { showErrorAlert, showSuccessAlert, showDeleteConfirmationAlert } from "../../utils/alertUtils";
import { formatEmailDate, formatFullEmailDate, formatDetailedEmailDate, extractSenderInfo, getEmailProviderTag } from "../../feature-module/application/components/email/emailUtils";
import { sanitizeAndScopeEmailContent } from "../../utils/emailStyleScopingUtils";
import male from "../../assets/male.png";
import { useTabVisibility } from "../../hooks/useTabVisibility";
import { useTenantChange } from "../../hooks/useTenantChange";
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import { Dropdown } from "bootstrap";

interface EmailProps {
  emails?: { label: string; content: React.ReactNode }[];
  onEmailChange?: (index: number, content: string) => void;
  contactIds?: number[]; // New prop for contact IDs
}

interface EmailChip {
  email: string;
  isValid: boolean;
}

const Email: React.FC<EmailProps> = ({ emails, onEmailChange, contactIds = [] }) => {
  const navigate = useNavigate();
  const { auth } = useContext(AuthContext);
  const [emailResponse, setEmailResponse] = useState<PaginatedEmailResponse>({
    emails: [],
    totalCount: 0,
    currentPage: 1,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | null>(null);
  const [showEmailDetail, setShowEmailDetail] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const actionDropdownRef = useRef<HTMLButtonElement>(null);
  const [processedEmailContent, setProcessedEmailContent] = useState<{ html: string; scopedCSS: string } | null>(null);
  // Reply state
  const [isReplyMode, setIsReplyMode] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [replyCc, setReplyCc] = useState('');
  const [replyCcChips, setReplyCcChips] = useState<EmailChip[]>([]);
  const [showReplyCc, setShowReplyCc] = useState(false);
  const [replyRecipient, setReplyRecipient] = useState('');
  const [replyRecipientChips, setReplyRecipientChips] = useState<EmailChip[]>([]);
  const [replyAttachments, setReplyAttachments] = useState<File[]>([]);
  const replyFileInputRef = useRef<HTMLInputElement>(null);
  const replyMessageRef = useRef<HTMLDivElement>(null);

  // Forward state
  const [isForwardMode, setIsForwardMode] = useState(false);
  const [forwardContent, setForwardContent] = useState('');
  const [forwardRecipient, setForwardRecipient] = useState('');
  const [forwardRecipientChips, setForwardRecipientChips] = useState<EmailChip[]>([]);
  const [sendingForward, setSendingForward] = useState(false);
  const [optionalMessage, setOptionalMessage] = useState('');
  const [forwardCc, setForwardCc] = useState('');
  const [forwardCcChips, setForwardCcChips] = useState<EmailChip[]>([]);
  const [showForwardCc, setShowForwardCc] = useState(false);
  const [forwardAttachments, setForwardAttachments] = useState<File[]>([]);
  const [isForwardDropdownOpen, setIsForwardDropdownOpen] = useState(true);
  const forwardFileInputRef = useRef<HTMLInputElement>(null);
  const forwardMessageRef = useRef<HTMLDivElement>(null);

  // Email actions state
  const [deletingEmail, setDeletingEmail] = useState(false);
  const [movingToTrash, setMovingToTrash] = useState(false);
  const [restoringFromTrash, setRestoringFromTrash] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Formatting states for reply
  const [showReplyEmojiPicker, setShowReplyEmojiPicker] = useState(false);
  const [showReplyLinkInput, setShowReplyLinkInput] = useState(false);
  const [showReplyFormatDropdown, setShowReplyFormatDropdown] = useState(false);
  const [replyLinkUrl, setReplyLinkUrl] = useState('');
  const [replyLinkText, setReplyLinkText] = useState('');
  const [replyActiveFormats, setReplyActiveFormats] = useState<Set<string>>(new Set());
  const [savedReplyRange, setSavedReplyRange] = useState<Range | null>(null);

  // Formatting states for forward
  const [showForwardEmojiPicker, setShowForwardEmojiPicker] = useState(false);
  const [showForwardLinkInput, setShowForwardLinkInput] = useState(false);
  const [showForwardFormatDropdown, setShowForwardFormatDropdown] = useState(false);
  const [forwardLinkUrl, setForwardLinkUrl] = useState('');
  const [forwardLinkText, setForwardLinkText] = useState('');
  const [forwardActiveFormats, setForwardActiveFormats] = useState<Set<string>>(new Set());
  const [savedForwardRange, setSavedForwardRange] = useState<Range | null>(null);

  // Use tab visibility hook for lazy loading
  const { isVisible, hasBeenActivated } = useTabVisibility({
    tabId: 'email',
    onTabActivated: () => {
      if (auth?.user_id) {
        fetchInboxEmails();
        setDataLoaded(true);
      }
    }
  });

  // Handle tenant change with hard reload
  useTenantChange({
    onTenantChange: () => {
      window.location.reload();
    }
  });

  const fetchInboxEmails = async (page: number = currentPage) => {
    try {
      setLoading(true);
      setError(null);

      const userId = auth?.user_id?.toString() || "";
      if (!userId) {
        setError("User ID not found");
        return;
      }

      // Use the new mail-conversation endpoint if contact IDs are provided
      if (contactIds && contactIds.length > 0) {
        const response = await EmailService.getMailConversation(userId, contactIds, page, 25);
        setEmailResponse(response);
      } else {
        // If no contact IDs are provided, show no emails (empty response)
        setEmailResponse({
          emails: [],
          totalCount: 0,
          currentPage: 1,
          hasNextPage: false,
          hasPreviousPage: false,
        });
      }
    } catch (err) {
      console.error("Error fetching inbox emails:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch inbox emails");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const handleCheckboxClick = (e: React.MouseEvent, emailId: string) => {
    e.stopPropagation();
    setSelectedEmails((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(emailId)) {
        newSet.delete(emailId);
      } else {
        newSet.add(emailId);
      }
      return newSet;
    });
  };

  const handleStarClickWithAPI = async (
    e: React.MouseEvent,
    emailId: string,
    isImportant: boolean
  ) => {
    e.stopPropagation();

    try {
      const emailIdNum = parseInt(emailId);
      const newImportanceStatus = await EmailService.toggleEmailImportance(
        emailIdNum,
        isImportant
      );

      setEmailResponse((prev) => ({
        ...prev,
        emails: prev.emails.map((email: any) =>
          email.mail_message_id.toString() === emailId
            ? { ...email, is_important: newImportanceStatus }
            : email
        ),
      }));

      if (newImportanceStatus) {
        showSuccessAlert('Email marked as important');
      } else {
        showSuccessAlert('Email unmarked as important');
      }
    } catch (err) {
      console.error("Error toggling star:", err);
      showErrorAlert("Failed to update star status");
    }
  };

  const handleSelectAll = () => {
    const allEmailIds = emailResponse.emails.map((email: any) =>
      email.mail_message_id.toString()
    );
    setSelectedEmails(new Set(allEmailIds));
  };

  const handleSelectNone = () => {
    setSelectedEmails(new Set());
  };

  const handleSelectRead = () => {
    const readEmailIds = emailResponse.emails
      .filter((email: any) => email.is_read)
      .map((email: any) => email.mail_message_id.toString());
    setSelectedEmails(new Set(readEmailIds));
  };

  const handleSelectUnread = () => {
    const unreadEmailIds = emailResponse.emails
      .filter((email: any) => !email.is_read)
      .map((email: any) => email.mail_message_id.toString());
    setSelectedEmails(new Set(unreadEmailIds));
  };

  const handleBulkOperation = async (
    operation: BulkMailOperationPayload["operation"]
  ) => {
    if (selectedEmails.size === 0) {
      showErrorAlert("Please select at least one email");
      return;
    }

    try {
      const mailMessageIds = Array.from(selectedEmails).map((id) =>
        parseInt(id)
      );

      await EmailService.bulkMailOperation({
        mail_message_ids: mailMessageIds,
        operation,
      });

      setEmailResponse((prev) => ({
        ...prev,
        emails: prev.emails
          .map((email: any) => {
            if (selectedEmails.has(email.mail_message_id.toString())) {
              const updatedEmail = { ...email };

              switch (operation) {
                case "mark_read":
                  updatedEmail.is_read = true;
                  break;
                case "mark_unread":
                  updatedEmail.is_read = false;
                  break;
                case "mark_important":
                  updatedEmail.is_important = true;
                  break;
                case "unmark_important":
                  updatedEmail.is_important = false;
                  break;
                case "trash":
                  updatedEmail.is_trash = true;
                  return null;
                case "untrash":
                  updatedEmail.is_trash = false;
                  return null;
                case "delete":
                  return null;
              }

              return updatedEmail;
            }
            return email;
          })
          .filter(Boolean) as EmailMessage[],
      }));

      setSelectedEmails(new Set());

      if (actionDropdownRef.current) {
        const dropdownElement = actionDropdownRef.current;
        const dropdownInstance = Dropdown.getInstance(dropdownElement);
        if (dropdownInstance) {
          dropdownInstance.hide();
        }
      }

      const operationMessages = {
        mark_read: "Emails marked as read",
        mark_unread: "Emails marked as unread",
        mark_important: "Emails marked as important",
        unmark_important: "Emails unmarked as important",
        trash: "Emails moved to trash",
        untrash: "Emails restored from trash",
        delete: "Emails deleted permanently",
      };

      showSuccessAlert(operationMessages[operation]);

    } catch (error) {
      console.error("Error performing bulk operation:", error);
      showErrorAlert("Failed to perform bulk operation");
    }
  };

  const handlePageChange = async (newPage: number) => {
    try {
      setPageLoading(true);
      setCurrentPage(newPage);
      const userId = auth?.user_id?.toString() || "";
      if (userId && contactIds && contactIds.length > 0) {
        const response = await EmailService.getMailConversation(userId, contactIds, newPage, 25);
        setEmailResponse(response);
      }
    } catch (err) {
      console.error("Error changing page:", err);
      showErrorAlert("Failed to load page");
    } finally {
      setPageLoading(false);
    }
  };

  // Load data when tab is activated
  useEffect(() => {
    if (hasBeenActivated && auth?.user_id) {
      fetchInboxEmails();
      setDataLoaded(true);
    }
  }, [hasBeenActivated, auth?.user_id, contactIds]);

  // Reset state when tab becomes visible (when switching from other tabs)
  useEffect(() => {
    const handleTabVisibility = () => {
      // Check if the email tab is currently visible
      const emailTab = document.querySelector('#email');
      if (emailTab && emailTab.classList.contains('show')) {
        // Set loading state first
        setInitialLoading(true);

        // Reset to list view when tab becomes visible
        setShowEmailDetail(false);
        setSelectedEmail(null);
        setIsReplyMode(false);
        setIsForwardMode(false);
        setReplyContent('');
        setForwardContent('');
        setReplyCc('');
        setForwardRecipient('');
        setOptionalMessage('');
        setReplyCcChips([]);
        setForwardRecipientChips([]);
        setForwardCcChips([]);
        setShowReplyCc(false);
        setShowForwardCc(false);
        setReplyAttachments([]);
        setForwardAttachments([]);
        setIsForwardDropdownOpen(true);
        setCurrentPage(1);
        setPageLoading(false);

        // Then fetch emails
        if (auth?.user_id) {
          fetchInboxEmails();
        }
      }
    };

    // Check immediately when component mounts
    handleTabVisibility();

    // Set up a MutationObserver to watch for tab changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          handleTabVisibility();
        }
      });
    });

    // Observe the email tab for class changes
    const emailTab = document.querySelector('#email');
    if (emailTab) {
      observer.observe(emailTab, { attributes: true });
    }

    // Also listen for Bootstrap tab events
    const handleTabShown = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target && target.id === 'email') {
        handleTabVisibility();
      }
    };

    document.addEventListener('shown.bs.tab', handleTabShown);

    return () => {
      observer.disconnect();
      document.removeEventListener('shown.bs.tab', handleTabShown);
    };
  }, []);

  // Process email content for scoped CSS when selectedEmail changes
  useEffect(() => {
    if (selectedEmail?.content) {
      const processed = sanitizeAndScopeEmailContent(selectedEmail.content, 'scoped-email-content');
      setProcessedEmailContent(processed);
    } else {
      setProcessedEmailContent(null);
    }
  }, [selectedEmail?.content]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text: string, maxLength: number = 50) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Helper function to validate single email address
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  // Helper function to create chips from email string
  const createChipsFromEmail = (emailString: string): EmailChip[] => {
    if (!emailString.trim()) return [];

    return emailString
      .split(/[,;\n]/)
      .map(email => email.trim())
      .filter(email => email.length > 0)
      .map(email => ({
        email: email,
        isValid: isValidEmail(email)
      }));
  };

  // Helper function to get all emails (chips + current input) as string array
  const getAllEmails = (chips: EmailChip[], currentInput: string): string[] => {
    const chipEmails = chips.filter(chip => chip.isValid).map(chip => chip.email);
    const inputEmails = currentInput.trim() ?
      currentInput.split(/[,;\n]/).map(email => email.trim()).filter(email => email.length > 0 && isValidEmail(email)) :
      [];
    return [...chipEmails, ...inputEmails];
  };

  const handleReplyCcChipCreation = () => {
    if (replyCc.trim()) {
      const newChips = createChipsFromEmail(replyCc);
      setReplyCcChips(prev => [...prev, ...newChips]);
      setReplyCc('');
    }
  };

  const handleReplyRecipientChipCreation = () => {
    if (replyRecipient.trim()) {
      const newChips = createChipsFromEmail(replyRecipient);
      setReplyRecipientChips(prev => [...prev, ...newChips]);
      setReplyRecipient('');
    }
  };

  // Handle chip creation for forward recipient field
  const handleForwardRecipientChipCreation = () => {
    if (forwardRecipient.trim()) {
      const newChips = createChipsFromEmail(forwardRecipient);
      setForwardRecipientChips(prev => [...prev, ...newChips]);
      setForwardRecipient('');
    }
  };

  // Handle chip creation for forward CC field
  const handleForwardCcChipCreation = () => {
    if (forwardCc.trim()) {
      const newChips = createChipsFromEmail(forwardCc);
      setForwardCcChips(prev => [...prev, ...newChips]);
      setForwardCc('');
    }
  };

  const handleReplyCcKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleReplyCcChipCreation();
    }
  };

  const handleReplyRecipientKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleReplyRecipientChipCreation();
    }
  };

  const handleForwardRecipientKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleForwardRecipientChipCreation();
    }
  };

  const handleForwardCcKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleForwardCcChipCreation();
    }
  };

  const removeReplyCcChip = (index: number) => {
    setReplyCcChips((prev: any) => prev.filter((_: any, i: number) => i !== index));
  };

  const removeReplyRecipientChip = (index: number) => {
    setReplyRecipientChips((prev: any) => prev.filter((_: any, i: number) => i !== index));
  };

  const removeForwardRecipientChip = (index: number) => {
    setForwardRecipientChips((prev: any) => prev.filter((_: any, i: number) => i !== index));
  };

  const removeForwardCcChip = (index: number) => {
    setForwardCcChips((prev: any) => prev.filter((_: any, i: number) => i !== index));
  };

  const handleEmailClick = async (emailId: string) => {
    const email = emailResponse.emails.find((e: any) => e.mail_message_id.toString() === emailId);
    if (email) {
      setSelectedEmail(email);
      setShowEmailDetail(true);

      // Mark as read if not already
      if (!email.is_read) {
        try {
          await EmailService.markEmailAsRead(email.mail_message_id);
          email.is_read = true;
        } catch (err) {
          console.error('Error marking as read:', err);
        }
      }
    }
  };

  const handleBackToList = () => {
    setShowEmailDetail(false);
    setSelectedEmail(null);
    setIsReplyMode(false);
    setIsForwardMode(false);
  };

  const handleStarClick = async () => {
    if (!selectedEmail) return;

    try {
      const newImportanceStatus = await EmailService.toggleEmailImportance(
        selectedEmail.mail_message_id,
        selectedEmail.is_important
      );
      setSelectedEmail((prev: any) => prev ? { ...prev, is_important: newImportanceStatus } : null);

      if (newImportanceStatus) {
        showSuccessAlert('Email marked as important');
      } else {
        showSuccessAlert('Email unmarked as important');
      }
    } catch (err) {
      console.error('Error toggling star:', err);
      showErrorAlert('Failed to update star status');
    }
  };

  const handleMoveToTrash = async () => {
    if (!selectedEmail || movingToTrash) return;

    try {
      setMovingToTrash(true);

      await EmailService.moveToTrash(selectedEmail.mail_message_id, {
        is_trash: true,
        is_important: false,
        is_read: true
      });

      showSuccessAlert('Email moved to trash successfully');

      // Reset to list view and reload fresh data
      setShowEmailDetail(false);
      setSelectedEmail(null);
      setIsReplyMode(false);
      setIsForwardMode(false);
      setReplyContent('');
      setForwardContent('');
      setReplyCc('');
      setForwardRecipient('');
      setOptionalMessage('');
      setReplyCcChips([]);
      setForwardRecipientChips([]);
      setForwardCcChips([]);
      setShowReplyCc(false);
      setShowForwardCc(false);
      setReplyAttachments([]);
      setForwardAttachments([]);
      setIsForwardDropdownOpen(true);

      // Set loading state and fetch fresh data
      setLoading(true);
      setInitialLoading(true);
      setCurrentPage(1);

      // Fetch fresh data
      if (auth?.user_id) {
        await fetchInboxEmails(1);
      }
    } catch (err) {
      console.error('Error moving email to trash:', err);
      showErrorAlert('Failed to move email to trash');
    } finally {
      setMovingToTrash(false);
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const handleDeleteEmail = async () => {
    if (!selectedEmail || deletingEmail) return;

    try {
      const result = await showDeleteConfirmationAlert(
        'Delete Email Permanently?',
        'Are you sure you want to permanently delete this email? This action cannot be undone.',
        'Yes, delete permanently',
        'Cancel'
      );

      if (!result.isConfirmed) return;

      setDeletingEmail(true);

      await EmailService.deleteEmail(selectedEmail.mail_message_id);

      showSuccessAlert('Email deleted successfully');

      // Reset to list view and reload fresh data
      setShowEmailDetail(false);
      setSelectedEmail(null);
      setIsReplyMode(false);
      setIsForwardMode(false);
      setReplyContent('');
      setForwardContent('');
      setReplyCc('');
      setForwardRecipient('');
      setOptionalMessage('');
      setReplyCcChips([]);
      setForwardRecipientChips([]);
      setForwardCcChips([]);
      setShowReplyCc(false);
      setShowForwardCc(false);
      setReplyAttachments([]);
      setForwardAttachments([]);
      setIsForwardDropdownOpen(true);

      // Set loading state and fetch fresh data
      setLoading(true);
      setInitialLoading(true);
      setCurrentPage(1);

      // Fetch fresh data
      if (auth?.user_id) {
        await fetchInboxEmails(1);
      }
    } catch (err) {
      console.error('Error deleting email:', err);
      showErrorAlert('Failed to delete email');
    } finally {
      setDeletingEmail(false);
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const handleMarkAsUnread = async () => {
    if (!selectedEmail) return;

    try {
      await EmailService.markEmailAsUnread(selectedEmail.mail_message_id);

      setSelectedEmail((prev: any) => prev ? { ...prev, is_read: false } : null);

      showSuccessAlert('Email marked as unread');
    } catch (err) {
      console.error('Error marking as unread:', err);
      showErrorAlert('Failed to mark email as unread');
    }
  };

  const handleReplyClick = () => {
    if (!selectedEmail) return;

    // Close forward interface if open
    if (isForwardMode) {
      setIsForwardMode(false);
      setForwardContent('');
      setForwardRecipient('');
      setOptionalMessage('');
      setForwardCc('');
      setShowForwardCc(false);
    }

    const senderInfo = extractSenderInfo(selectedEmail.send_from);
    const initialToEmail = senderInfo.email || selectedEmail.send_from || '';
    if (initialToEmail) {
      setReplyRecipientChips(createChipsFromEmail(initialToEmail));
    } else {
      setReplyRecipientChips([]);
    }
    setReplyRecipient('');
    setReplyCc('');
    setReplyCcChips([]);
    setShowReplyCc(false);
    setReplyAttachments([]);

    setIsReplyMode(true);
    setReplyContent('');

    // Scroll to reply section after a short delay
    setTimeout(() => {
      const replyContainer = document.querySelector('.email-reply-container');
      if (replyContainer) {
        replyContainer.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  const handleSendReply = async () => {
    if (!selectedEmail || !replyContent.trim()) return;

    let allRecipientEmails = getAllEmails(replyRecipientChips, replyRecipient);
    if (allRecipientEmails.length === 0) {
      showErrorAlert('Please enter at least one valid recipient email');
      return;
    }

    try {
      setSendingReply(true);
      const replySubject = selectedEmail.subject.startsWith('Re:') ? selectedEmail.subject : `Re: ${selectedEmail.subject}`;

      const userId = auth?.user_id?.toString() || '';
      const providers = await EmailService.getMailProviders(userId);

      if (providers.length === 0) {
        showErrorAlert('No email providers available');
        return;
      }

      const selectedProvider = providers[0];
      const cleanHtml = replyContent
        .replace(/ data-start="[^"]*"/g, '')
        .replace(/ data-end="[^"]*"/g, '');

      const hasHtmlTags = /<[^>]+>/.test(cleanHtml);

      const payload: SendEmailPayload = {
        to: allRecipientEmails,
        subject: replySubject,
        body: cleanHtml,
        is_html: hasHtmlTags,
        user_id: userId
      };

      // Get all CC emails (chips + current input)
      const allCcEmails = getAllEmails(replyCcChips, replyCc);
      if (allCcEmails.length > 0) {
        payload.cc = allCcEmails;
      }

      // Send email with binary attachments using FormData
      if (replyAttachments.length > 0) {
        // Use FormData for binary attachment upload
        const formData = new FormData();

        // Add email data as form fields
        formData.append('to', JSON.stringify(payload.to));
        if (payload.cc) {
          formData.append('cc', JSON.stringify(payload.cc));
        }
        formData.append('subject', payload.subject);
        formData.append('body', payload.body);
        formData.append('is_html', payload.is_html.toString());
        formData.append('user_id', payload.user_id);

        // Add binary attachments
        replyAttachments.forEach((file, index) => {
          formData.append('attachments', file);
        });

        await EmailService.sendEmailWithBinaryAttachments(selectedProvider.mail_provider_key, formData);
      } else {
        // Send regular JSON payload without attachments
        await EmailService.sendEmail(selectedProvider.mail_provider_key, payload);
      }

      showSuccessAlert('Reply sent successfully!');
      setIsReplyMode(false);
      setReplyContent('');
      handleBackToList();
    } catch (error) {
      console.error('Error sending reply:', error);
      showErrorAlert('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const handleCancelReply = () => {
    setIsReplyMode(false);
    setReplyContent('');
    setReplyRecipient('');
    setReplyRecipientChips([]);
    setReplyCc('');
    setReplyCcChips([]);
    setShowReplyCc(false);
    setReplyAttachments([]);
  };

  const handleForwardClick = () => {
    if (!selectedEmail) return;

    // Close reply interface if open
    if (isReplyMode) {
      setIsReplyMode(false);
      setReplyContent('');
      setReplyCc('');
      setShowReplyCc(false);
    }

    const forwardSubject = selectedEmail.subject.startsWith('Fwd:') ? selectedEmail.subject : `Fwd: ${selectedEmail.subject}`;

    // Use the original email content for display
    setIsForwardMode(true);
    setForwardContent(selectedEmail.content);
    setForwardRecipient('');
    setOptionalMessage('');
    setForwardAttachments([]); // Clear existing attachments first

    // Load original email attachments
    if (selectedEmail.attachments && selectedEmail.attachments.length > 0) {
      selectedEmail.attachments.forEach(async (attachment: any) => {
        const url = attachment.attachement_url || attachment.attachment_url || '';
        if (url) {
          try {
            const response = await fetch(url);
            if (response.ok) {
              const blob = await response.blob();
              const fileName = url.split('/').pop() || `attachment-${Date.now()}`;
              const file = new File([blob], fileName, { type: blob.type });
              setForwardAttachments(prev => [...prev, file]);
            }
          } catch (error) {
            console.error('Error loading attachment for forward:', error);
          }
        }
      });
    }

    // Scroll to forward section after a short delay
    setTimeout(() => {
      const forwardContainer = document.querySelector('.email-forward-container');
      if (forwardContainer) {
        forwardContainer.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  const handleSendForward = async () => {
    if (!selectedEmail) return;

    // Get all recipient emails directly (including current input and existing chips)
    let allRecipientEmails = getAllEmails(forwardRecipientChips, forwardRecipient);

    // If no valid emails found, show error
    if (allRecipientEmails.length === 0) {
      showErrorAlert('Please enter at least one valid recipient email');
      return;
    }

    try {
      setSendingForward(true);

      const forwardSubject = selectedEmail.subject.startsWith('Fwd:') ? selectedEmail.subject : `Fwd: ${selectedEmail.subject}`;

      // Get the first available provider for the current user
      const userId = auth?.user_id?.toString() || '';

      const providers = await EmailService.getMailProviders(userId);

      if (providers.length === 0) {
        showErrorAlert('No email providers available');
        return;
      }

      const selectedProvider = providers[0];

      // Create Gmail-style forwarded message HTML
      const forwardedMessageHtml = `
<div style="border-left: 2px solid #ccc; padding-left: 12px; margin: 16px 0; color: #666; font-size: 13px;">
  <div style="margin-bottom: 8px; color: #999; font-size: 12px; border-bottom: 1px solid #eee; padding-bottom: 8px;">
    ---------- Forwarded message ---------
  </div>
  <div style="margin-bottom: 4px;"><strong>From:</strong> ${selectedEmail.send_from}</div>
  <div style="margin-bottom: 4px;"><strong>Date:</strong> ${formatFullEmailDate(selectedEmail.email_datetime)}</div>
  <div style="margin-bottom: 4px;"><strong>Subject:</strong> ${selectedEmail.subject}</div>
  <div style="margin-bottom: 4px;"><strong>To:</strong> ${selectedEmail.send_to}</div>
  ${selectedEmail.mail_cc ? `<div style="margin-bottom: 4px;"><strong>CC:</strong> ${selectedEmail.mail_cc}</div>` : ''}
  <div style="margin-top: 12px; border-top: 1px solid #eee; padding-top: 12px;">
    ${selectedEmail.content}
  </div>
</div>
      `.trim();

      // Combine optional message with forwarded content
      const combinedContent = optionalMessage.trim()
        ? `<div style="margin-bottom: 16px;">${optionalMessage.trim().replace(/\n/g, '<br>')}</div>${forwardedMessageHtml}`
        : forwardedMessageHtml;

      const cleanHtml = combinedContent
        .replace(/ data-start="[^"]*"/g, '')
        .replace(/ data-end="[^"]*"/g, '');

      const payload: SendEmailPayload = {
        to: allRecipientEmails,
        subject: forwardSubject,
        body: cleanHtml,
        is_html: true, // Always HTML for forwarded messages
        user_id: userId
      };

      // Get all CC emails (chips + current input)
      const allCcEmails = getAllEmails(forwardCcChips, forwardCc);
      if (allCcEmails.length > 0) {
        payload.cc = allCcEmails;
      }

      // Send email with binary attachments using FormData
      if (forwardAttachments.length > 0) {
        // Use FormData for binary attachment upload
        const formData = new FormData();

        // Add email data as form fields
        formData.append('to', JSON.stringify(payload.to));
        if (payload.cc) {
          formData.append('cc', JSON.stringify(payload.cc));
        }
        formData.append('subject', payload.subject);
        formData.append('body', payload.body);
        formData.append('is_html', payload.is_html.toString());
        formData.append('user_id', payload.user_id);

        // Add binary attachments
        forwardAttachments.forEach((file, index) => {
          formData.append('attachments', file);
        });

        await EmailService.sendEmailWithBinaryAttachments(selectedProvider.mail_provider_key, formData);
      } else {
        // Send regular JSON payload without attachments
        await EmailService.sendEmail(selectedProvider.mail_provider_key, payload);
      }

      showSuccessAlert('Email forwarded successfully!');
      setIsForwardMode(false);
      setForwardContent('');
      setForwardRecipient('');
      setForwardRecipientChips([]);
      setOptionalMessage('');
      setForwardCc('');
      setForwardCcChips([]);
      setShowForwardCc(false);
      setForwardAttachments([]);
      handleBackToList();
    } catch (error) {
      console.error('Error forwarding email:', error);
      showErrorAlert('Failed to forward email');
    } finally {
      setSendingForward(false);
    }
  };

  const handleCancelForward = () => {
    setIsForwardMode(false);
    setForwardContent('');
    setForwardRecipient('');
    setForwardRecipientChips([]);
    setOptionalMessage('');
    setForwardCc('');
    setForwardCcChips([]);
    setShowForwardCc(false);
    setForwardAttachments([]);
    setIsForwardDropdownOpen(true);
  };

  // File handling functions for reply
  const handleReplyFileClick = () => {
    replyFileInputRef.current?.click();
  };

  const handleReplyFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setReplyAttachments(prev => [...prev, ...files]);
    }
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleRemoveReplyAttachment = (index: number) => {
    setReplyAttachments((prev: any) => prev.filter((_: any, i: number) => i !== index));
  };

  // File handling functions for forward
  const handleForwardFileClick = () => {
    forwardFileInputRef.current?.click();
  };

  const handleForwardFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setForwardAttachments(prev => [...prev, ...files]);
    }
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleRemoveForwardAttachment = (index: number) => {
    setForwardAttachments((prev: any) => prev.filter((_: any, i: number) => i !== index));
  };

  const handleFileDownload = async (fileUrl: string, fileName: string) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
      showErrorAlert('Failed to download file');
    }
  };

  // Formatting helper functions for reply
  const saveReplyRange = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      setSavedReplyRange(selection.getRangeAt(0).cloneRange());
    }
  };

  const restoreReplyRange = () => {
    if (savedReplyRange && replyMessageRef.current) {
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(savedReplyRange);
      replyMessageRef.current.focus();
    }
  };

  const execReplyCommand = (command: string, value?: string) => {
    saveReplyRange();
    restoreReplyRange();
    document.execCommand(command, false, value);
    replyMessageRef.current?.focus();
  };

  const handleReplyFormatAction = (action: string, value?: string) => {
    switch (action) {
      case 'bold':
        execReplyCommand('bold');
        break;
      case 'italic':
        execReplyCommand('italic');
        break;
      case 'underline':
        execReplyCommand('underline');
        break;
      case 'strikethrough':
        execReplyCommand('strikethrough');
        break;
      case 'justifyLeft':
        execReplyCommand('justifyLeft');
        break;
      case 'justifyCenter':
        execReplyCommand('justifyCenter');
        break;
      case 'justifyRight':
        execReplyCommand('justifyRight');
        break;
      case 'justifyFull':
        execReplyCommand('justifyFull');
        break;
      case 'insertUnorderedList':
        execReplyCommand('insertUnorderedList');
        break;
      case 'insertOrderedList':
        execReplyCommand('insertOrderedList');
        break;
      case 'indent':
        execReplyCommand('indent');
        break;
      case 'outdent':
        execReplyCommand('outdent');
        break;
      case 'removeFormat':
        execReplyCommand('removeFormat');
        setReplyActiveFormats(new Set());
        break;
    }
  };

  const handleReplyEmojiClick = (emojiData: EmojiClickData) => {
    if (replyMessageRef.current) {
      restoreReplyRange();

      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(document.createTextNode(emojiData.emoji));

        // Move cursor to end of emoji
        range.setStartAfter(range.endContainer);
        range.setEndAfter(range.endContainer);
        selection.removeAllRanges();
        selection.addRange(range);
      }

      replyMessageRef.current.focus();
      setReplyContent(replyMessageRef.current.innerHTML);
    }

    setShowReplyEmojiPicker(false);
  };

  const handleReplyInsertLink = () => {
    if (!replyLinkUrl) return;

    if (replyMessageRef.current) {
      restoreReplyRange();

      const link = document.createElement('a');
      const url = replyLinkUrl.trim();
      const absoluteUrl = url.startsWith('http://') || url.startsWith('https://')
        ? url
        : `https://${url}`;

      link.href = absoluteUrl;
      link.textContent = replyLinkText || replyLinkUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.color = '#0066cc';
      link.style.textDecoration = 'underline';

      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(link);

        // Add a space after the link
        const space = document.createTextNode(' ');
        range.setStartAfter(link);
        range.insertNode(space);

        // Move cursor after the space
        range.setStartAfter(space);
        range.setEndAfter(space);
        selection.removeAllRanges();
        selection.addRange(range);
      }

      setReplyContent(replyMessageRef.current.innerHTML);
      replyMessageRef.current.focus();
    }

    setShowReplyLinkInput(false);
    setReplyLinkUrl('');
    setReplyLinkText('');
  };

  // Formatting helper functions for forward
  const saveForwardRange = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      setSavedForwardRange(selection.getRangeAt(0).cloneRange());
    }
  };

  const restoreForwardRange = () => {
    if (savedForwardRange && forwardMessageRef.current) {
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(savedForwardRange);
      forwardMessageRef.current.focus();
    }
  };

  const execForwardCommand = (command: string, value?: string) => {
    saveForwardRange();
    restoreForwardRange();
    document.execCommand(command, false, value);
    forwardMessageRef.current?.focus();
  };

  const handleForwardFormatAction = (action: string, value?: string) => {
    switch (action) {
      case 'bold':
        execForwardCommand('bold');
        break;
      case 'italic':
        execForwardCommand('italic');
        break;
      case 'underline':
        execForwardCommand('underline');
        break;
      case 'strikethrough':
        execForwardCommand('strikethrough');
        break;
      case 'justifyLeft':
        execForwardCommand('justifyLeft');
        break;
      case 'justifyCenter':
        execForwardCommand('justifyCenter');
        break;
      case 'justifyRight':
        execForwardCommand('justifyRight');
        break;
      case 'justifyFull':
        execForwardCommand('justifyFull');
        break;
      case 'insertUnorderedList':
        execForwardCommand('insertUnorderedList');
        break;
      case 'insertOrderedList':
        execForwardCommand('insertOrderedList');
        break;
      case 'indent':
        execForwardCommand('indent');
        break;
      case 'outdent':
        execForwardCommand('outdent');
        break;
      case 'removeFormat':
        execForwardCommand('removeFormat');
        setForwardActiveFormats(new Set());
        break;
    }
  };

  const handleForwardEmojiClick = (emojiData: EmojiClickData) => {
    if (forwardMessageRef.current) {
      restoreForwardRange();

      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(document.createTextNode(emojiData.emoji));

        // Move cursor to end of emoji
        range.setStartAfter(range.endContainer);
        range.setEndAfter(range.endContainer);
        selection.removeAllRanges();
        selection.addRange(range);
      }

      forwardMessageRef.current.focus();
      setOptionalMessage(forwardMessageRef.current.innerHTML);
    }

    setShowForwardEmojiPicker(false);
  };

  const handleForwardInsertLink = () => {
    if (!forwardLinkUrl) return;

    if (forwardMessageRef.current) {
      restoreForwardRange();

      const link = document.createElement('a');
      const url = forwardLinkUrl.trim();
      const absoluteUrl = url.startsWith('http://') || url.startsWith('https://')
        ? url
        : `https://${url}`;

      link.href = absoluteUrl;
      link.textContent = forwardLinkText || forwardLinkUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.color = '#0066cc';
      link.style.textDecoration = 'underline';

      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(link);

        // Add a space after the link
        const space = document.createTextNode(' ');
        range.setStartAfter(link);
        range.insertNode(space);

        // Move cursor after the space
        range.setStartAfter(space);
        range.setEndAfter(space);
        selection.removeAllRanges();
        selection.addRange(range);
      }

      setOptionalMessage(forwardMessageRef.current.innerHTML);
      forwardMessageRef.current.focus();
    }

    setShowForwardLinkInput(false);
    setForwardLinkUrl('');
    setForwardLinkText('');
  };

  return (
    <div className="tab-pane fade" id="email">
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
          <h4 className="fw-semibold">
            {showEmailDetail ? (
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-sm me-2"
                  onClick={handleBackToList}
                >
                  <i className="fas fa-arrow-left text-primary"></i>
                </button>
                Email Detail
              </div>
            ) : (
              "Email"
            )}
          </h4>
          {!showEmailDetail && !initialLoading && emailResponse.emails.length > 0 && (
            <div className="d-flex align-items-center gap-2">
              {selectedEmails.size > 0 && (
                <div className="btn-group dropdown-action me-1">
                  <button
                    ref={actionDropdownRef}
                    type="button"
                    className="btn btn-outline-primary dropdown-toggle btn-sm"
                    data-bs-toggle="dropdown"
                  >
                    Actions
                  </button>
                  <div className="dropdown-menu shadow">
                    <button
                      className="dropdown-item"
                      onClick={() => handleBulkOperation("mark_read")}
                    >
                      <i className="fas fa-check me-2"></i>
                      Mark As Read
                    </button>
                    <button
                      className="dropdown-item"
                      onClick={() => handleBulkOperation("mark_unread")}
                    >
                      <i className="fas fa-envelope me-2"></i>
                      Mark As Unread
                    </button>
                    <div className="dropdown-divider" />
                    <button
                      className="dropdown-item"
                      onClick={() => handleBulkOperation("mark_important")}
                    >
                      <i className="fas fa-star me-2"></i>
                      Mark As Important
                    </button>
                    <button
                      className="dropdown-item"
                      onClick={() => handleBulkOperation("unmark_important")}
                    >
                      <i className="far fa-star me-2"></i>
                      Unmark As Important
                    </button>
                    <div className="dropdown-divider" />
                    <button
                      className="dropdown-item text-danger"
                      onClick={() => handleBulkOperation("trash")}
                    >
                      <i className="fas fa-trash me-2"></i>
                      Delete
                    </button>
                  </div>
                </div>
              )}
              <div className="btn-group">
                <button
                  className="btn btn-sm"
                  disabled={!emailResponse.hasPreviousPage || pageLoading}
                  onClick={() => handlePageChange(currentPage - 1)}
                  style={{
                    color: !emailResponse.hasPreviousPage || pageLoading ? '#6c757d' : '#000000',
                    borderColor: '#6c757d',
                    backgroundColor: 'transparent',
                    border: '1px solid #6c757d'
                  }}
                >
                  <i
                    className="fas fa-angle-left"
                    style={{
                      color: !emailResponse.hasPreviousPage || pageLoading ? '#6c757d' : '#000000'
                    }}
                  />
                </button>
                <button
                  className="btn btn-sm"
                  disabled={!emailResponse.hasNextPage || pageLoading}
                  onClick={() => handlePageChange(currentPage + 1)}
                  style={{
                    color: !emailResponse.hasNextPage || pageLoading ? '#6c757d' : '#000000',
                    borderColor: '#6c757d',
                    backgroundColor: 'transparent',
                    border: '1px solid #6c757d'
                  }}
                >
                  {pageLoading ? (
                    <i
                      className="fas fa-spinner fa-spin"
                      style={{ color: !emailResponse.hasNextPage || pageLoading ? '#6c757d' : '#000000' }}
                    />
                  ) : (
                    <i
                      className="fas fa-angle-right"
                      style={{ color: !emailResponse.hasNextPage || pageLoading ? '#6c757d' : '#000000' }}
                    />
                  )}
                </button>
              </div>
              <span className="text-muted small">
                {(() => {
                  const pageSize = 25;
                  const totalCount = emailResponse.totalCount;
                  if (totalCount <= pageSize) {
                    return `${totalCount} out of ${totalCount}`;
                  } else {
                    const currentPageEnd = Math.min(currentPage * pageSize, totalCount);
                    return `${currentPageEnd} out of ${totalCount}`;
                  }
                })()}
              </span>
            </div>
          )}
        </div>
        <div className="card-body">
          {initialLoading || loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading inbox emails...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              <i className="ti ti-alert-circle me-2"></i>
              {error}
            </div>
          ) : !initialLoading && emailResponse.emails.length === 0 ? (
            <div className="card border mb-0">
              <div className="card-body pb-0">
                <div className="row align-items-center">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <h4 className="fw-medium mb-1">
                        {contactIds.length > 0 ? "No Emails Found" : "No Emails"}
                      </h4>
                      <p>
                        {contactIds.length > 0
                          ? "No emails found for this contact."
                          : "You don't have any emails yet."
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : showEmailDetail && selectedEmail ? (
            <div className="email-detail">
              {/* Email Actions Bar */}
              <div className="card mb-4">
                <div className="card-body py-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="left-actions d-flex align-items-center">
                      <button
                        className="btn btn-icon me-2"
                        title="Move to trash"
                        onClick={handleMoveToTrash}
                        disabled={movingToTrash || deletingEmail || restoringFromTrash}
                      >
                        {movingToTrash ? (
                          <i className="fas fa-spinner fa-spin"></i>
                        ) : (
                          <i className="fas fa-trash-alt"></i>
                        )}
                      </button>
                      {/* Removed permanent delete button */}
                      <button
                        className="btn btn-icon me-2"
                        title="Mark unread"
                        onClick={handleMarkAsUnread}
                      >
                        <i className="fas fa-envelope"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Email Content - Make this scrollable */}
              <div className="card" style={{ maxHeight: '70vh', overflow: 'auto' }}>
                <div className="card-body p-4">
                  <div className="email-content-header mb-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h4 className="email-title mb-0">{selectedEmail.subject}</h4>
                      <span className="text-muted small">
                        {formatDetailedEmailDate(selectedEmail.email_datetime)}
                      </span>
                    </div>
                    <div className="email-info-bar d-flex align-items-center mt-4" style={{ height: '50px' }}>
                      <div className="sender-avatar me-2">
                        <div className="avatar avatar-sm">
                          <img
                            src={male}
                            alt="sender"
                            className="rounded-circle"
                            width="32"
                            height="32"
                            style={{ objectFit: 'cover' }}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const parent = e.currentTarget.parentElement;
                              if (parent && !parent.querySelector('.avatar-fallback')) {
                                const fallback = document.createElement('div');
                                fallback.className = 'avatar-fallback d-flex align-items-center justify-content-center bg-light rounded-circle';
                                fallback.style.width = '32px';
                                fallback.style.height = '32px';
                                fallback.style.backgroundColor = '#f8f9fa';
                                fallback.innerHTML = '<i class="ti ti-user" style="font-size: 16px; color: #6c757d;"></i>';
                                parent.appendChild(fallback);
                              }
                            }}
                          />
                        </div>
                      </div>
                      <div className="email-meta flex-grow-1" style={{ height: '30px' }}>
                        <div className="d-flex justify-content-between align-items-center" style={{ height: '30px' }}>
                          <div>
                            {(() => {
                              const senderInfo = extractSenderInfo(selectedEmail.send_from);
                              const providerTag = getEmailProviderTag(selectedEmail.mail_provider_key);
                              return (
                                <>
                                  <span className="sender-name">{senderInfo.name}</span>
                                  <span className="text-muted ms-1">&lt;{senderInfo.email}&gt;</span>
                                  <div className="text-muted small d-flex align-items-center position-relative" ref={dropdownRef}>
                                    to {selectedEmail.send_to}
                                    <span
                                      className="badge ms-2"
                                      style={{
                                        backgroundColor: providerTag.bgColor,
                                        color: providerTag.color,
                                        fontSize: '0.6rem',
                                        padding: '0.15rem 0.3rem',
                                        borderRadius: '3px'
                                      }}
                                    >
                                      <i className={providerTag.icon} style={{ marginRight: '0.15rem', fontSize: '0.7rem' }}></i>
                                      {providerTag.label}
                                    </span>
                                    <button className="btn btn-icon btn-sm ms-2" title="Show details" onClick={() => setShowDetails(v => !v)}>
                                      <i className={`fas fa-chevron-${showDetails ? 'up' : 'down'}`}></i>
                                    </button>
                                    {showDetails && (
                                      <div className="email-details-box bg-white border rounded p-3 shadow-sm" style={{
                                        zIndex: 1000,
                                        position: 'absolute',
                                        top: '100%',
                                        left: '0',
                                        minWidth: '450px',
                                        marginTop: '5px',
                                        fontSize: '0.875rem',
                                        color: '#000000'
                                      }}>
                                        <div className="email-detail-item mb-2">
                                          <div className="d-flex">
                                            <div className="text-muted fw-medium" style={{ minWidth: '60px', flexShrink: 0 }}>From:</div>
                                            <div className="flex-grow-1">{senderInfo.name} &lt;{senderInfo.email}&gt;</div>
                                          </div>
                                        </div>
                                        <div className="email-detail-item mb-2">
                                          <div className="d-flex">
                                            <div className="text-muted fw-medium" style={{ minWidth: '60px', flexShrink: 0 }}>To:</div>
                                            <div className="flex-grow-1">{selectedEmail.send_to}</div>
                                          </div>
                                        </div>
                                        {selectedEmail.mail_cc && (
                                          <div className="email-detail-item mb-2">
                                            <div className="d-flex">
                                              <div className="text-muted fw-medium" style={{ minWidth: '60px', flexShrink: 0 }}>CC:</div>
                                              <div className="flex-grow-1">{selectedEmail.mail_cc}</div>
                                            </div>
                                          </div>
                                        )}
                                        <div className="email-detail-item mb-2">
                                          <div className="d-flex">
                                            <div className="text-muted fw-medium" style={{ minWidth: '60px', flexShrink: 0 }}>Date:</div>
                                            <div className="flex-grow-1">{formatFullEmailDate(selectedEmail.email_datetime)}</div>
                                          </div>
                                        </div>
                                        <div className="email-detail-item mb-0">
                                          <div className="d-flex">
                                            <div className="text-muted fw-medium" style={{ minWidth: '60px', flexShrink: 0 }}>Subject:</div>
                                            <div className="flex-grow-1">{selectedEmail.subject}</div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                          <div className="email-actions d-flex align-items-center">
                            <button className="btn btn-icon btn-sm me-1 p-0" title="Star" onClick={handleStarClick}>
                              <i className={`fas fa-star ${selectedEmail.is_important ? "text-warning" : ""}`}></i>
                            </button>
                            <button className="btn btn-icon btn-sm p-0" title="Reply" onClick={handleReplyClick}>
                              <i className="fas fa-reply"></i>
                            </button>
                            <button className="btn btn-icon btn-sm p-0 ms-1" title="Forward" onClick={handleForwardClick}>
                              <i className="fas fa-share"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="email-body mt-4">
                    <div
                      className="scoped-email-content"
                      dangerouslySetInnerHTML={{ __html: processedEmailContent?.html || selectedEmail.content }}
                      style={{
                        lineHeight: '1.6',
                        fontSize: '14px',
                        color: '#202124',
                        fontFamily: 'inherit'
                      }}
                    />

                    {/* Scoped CSS Styles for Email Content */}
                    {processedEmailContent?.scopedCSS && (
                      <style dangerouslySetInnerHTML={{
                        __html: processedEmailContent.scopedCSS
                      }} />
                    )}

                    {/* Fallback CSS Styles for Email Content Formatting when no processed content */}
                    {!processedEmailContent && (
                      <style dangerouslySetInnerHTML={{
                        __html: `
                          .scoped-email-content b,
                          .scoped-email-content strong {
                          font-weight: bold !important;
                        }
                        
                          .scoped-email-content i,
                          .scoped-email-content em {
                          font-style: italic !important;
                        }
                        
                          .scoped-email-content u {
                          text-decoration: underline !important;
                        }
                        
                          .scoped-email-content s,
                          .scoped-email-content strike {
                          text-decoration: line-through !important;
                        }
                        
                          .scoped-email-content a {
                          color: #1a73e8 !important;
                          text-decoration: underline !important;
                          cursor: pointer !important;
                        }
                        
                          .scoped-email-content a:hover {
                          text-decoration: underline !important;
                          color: #0d5ec7 !important;
                        }
                        
                          .scoped-email-content ul {
                          list-style-type: disc !important;
                          margin: 16px 0 !important;
                          padding-left: 40px !important;
                          display: block !important;
                        }
                        
                          .scoped-email-content ol {
                          list-style-type: decimal !important;
                          margin: 16px 0 !important;
                          padding-left: 40px !important;
                          display: block !important;
                        }
                        
                          .scoped-email-content li {
                          margin: 4px 0 !important;
                          list-style-position: outside !important;
                          display: list-item !important;
                        }
                        
                          .scoped-email-content p {
                          margin: 8px 0 !important;
                        }
                        
                          .scoped-email-content br {
                          line-height: 1.6 !important;
                        }
                        
                          .scoped-email-content div {
                          margin: 0 !important;
                        }
                        
                          .scoped-email-content blockquote {
                          border-left: 3px solid #ccc !important;
                          margin: 16px 0 !important;
                          padding-left: 12px !important;
                          color: #666 !important;
                        }
                        
                          .scoped-email-content h1, .scoped-email-content h2, .scoped-email-content h3, 
                          .scoped-email-content h4, .scoped-email-content h5, .scoped-email-content h6 {
                          margin: 16px 0 8px 0 !important;
                          font-weight: bold !important;
                        }
                        
                          .scoped-email-content pre {
                          background-color: #f8f9fa !important;
                          border: 1px solid #e9ecef !important;
                          border-radius: 4px !important;
                          padding: 8px !important;
                          overflow-x: auto !important;
                          font-family: monospace !important;
                        }
                        
                          .scoped-email-content code {
                          background-color: #f8f9fa !important;
                          padding: 2px 4px !important;
                          border-radius: 3px !important;
                          font-family: monospace !important;
                        }
                      `
                      }} />
                    )}

                    {/* Gmail-style Attachments Section */}
                    {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                      <div className="email-attachments mt-4 pt-4 border-top">
                        <div className="gmail-attachment-header">
                          <div className="gmail-attachment-header-content">
                            <i className="fas fa-paperclip gmail-attachment-icon"></i>
                            <span className="gmail-attachment-count">
                              {selectedEmail.attachments.length} Attachment{selectedEmail.attachments.length > 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                        <div className="gmail-attachments-container">
                          {selectedEmail.attachments.map((attachment: any, index: number) => {
                            // Extract file extension and name from URL
                            const url = attachment.attachement_url || attachment.attachment_url || '';

                            // Skip if no URL is available
                            if (!url) {
                              return null;
                            }

                            const fileName = url.split('/').pop() || `attachment-${index + 1}`;
                            const extension = fileName.split('.').pop()?.toUpperCase() || 'FILE';

                            // Get file icon and color based on extension
                            const getFileIcon = (ext: string) => {
                              const extLower = ext.toLowerCase();
                              if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extLower)) {
                                return { icon: 'far fa-file-image', color: '#6F42C1' };
                              } else if (['pdf'].includes(extLower)) {
                                return { icon: 'far fa-file-pdf', color: '#DC3545' };
                              } else if (['doc', 'docx'].includes(extLower)) {
                                return { icon: 'far fa-file-word', color: '#0D6EFD' };
                              } else if (['xls', 'xlsx'].includes(extLower)) {
                                return { icon: 'far fa-file-excel', color: '#198754' };
                              } else if (['ppt', 'pptx'].includes(extLower)) {
                                return { icon: 'far fa-file-powerpoint', color: '#DC3545' };
                              } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extLower)) {
                                return { icon: 'far fa-file-archive', color: '#FD7E14' };
                              } else if (['mp4', 'avi', 'mov', 'wmv', 'mkv', 'flv'].includes(extLower)) {
                                return { icon: 'far fa-file-video', color: '#FD7E14' };
                              } else if (['mp3', 'wav', 'flac', 'aac'].includes(extLower)) {
                                return { icon: 'far fa-file-audio', color: '#6F42C1' };
                              } else if (['txt', 'rtf'].includes(extLower)) {
                                return { icon: 'far fa-file-alt', color: '#6C757D' };
                              } else {
                                return { icon: 'far fa-file', color: '#6C757D' };
                              }
                            };

                            const fileInfo = getFileIcon(extension);

                            return (
                              <div key={attachment.id || index} className="gmail-attachment-item">
                                <div className="gmail-attachment-content">
                                  <div className="gmail-attachment-icon">
                                    <div className="gmail-file-icon" style={{ backgroundColor: fileInfo.color }}>
                                      <i className={fileInfo.icon}></i>
                                    </div>
                                  </div>
                                  <div className="gmail-attachment-info">
                                    <div className="gmail-file-name">{fileName}</div>
                                    <div className="gmail-file-type">
                                      {extension} • {attachment.size_in_kb ? `${attachment.size_in_kb} KB` : 'Unknown size'}
                                    </div>
                                  </div>
                                  <div className="gmail-attachment-actions">
                                    <button
                                      onClick={() => handleFileDownload(url, fileName)}
                                      className="gmail-download-btn"
                                      title="Download"
                                    >
                                      <i className="fas fa-download"></i>
                                    </button>
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="gmail-open-btn"
                                      title="Open in new tab"
                                    >
                                      <i className="fas fa-external-link-alt"></i>
                                    </a>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="email-actions-bottom mt-3 pt-3 border-top">
                      <div className="d-flex gap-2">
                        <button className="btn btn-primary" onClick={handleReplyClick}>
                          <i className="fas fa-reply me-2"></i>Reply
                        </button>
                        <button className="btn btn-primary" onClick={handleForwardClick}>
                          <i className="fas fa-share me-2"></i>Forward
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Gmail-style Reply Interface */}
                  {isReplyMode && selectedEmail && (
                    <div className="email-reply-container mt-4 pt-4 border-top">
                      <div className="reply-header mb-3">
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="reply-info w-100">
                            <div className="reply-to mb-1 d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center" style={{ flex: '1', width: '100%' }}>
                                <span className="text-muted fw-medium me-2" style={{ minWidth: '30px' }}>To:</span>
                                <div className="email-input-container d-flex flex-wrap align-items-center gap-1" style={{ flex: '1', width: '100%', maxWidth: 'calc(100% - 70px)' }}>
                                  {replyRecipientChips.map((chip, index) => (
                                    <span
                                      key={index}
                                      className={`email-chip ${chip.isValid ? 'valid' : 'invalid'}`}
                                    >
                                      {chip.email}
                                      <button
                                        className="chip-remove"
                                        onClick={() => removeReplyRecipientChip(index)}
                                        type="button"
                                      >
                                        ×
                                      </button>
                                    </span>
                                  ))}
                                  <input
                                    type="email"
                                    className="form-control form-control-sm chip-input"
                                    style={{ border: 'none', outline: 'none', boxShadow: 'none', flex: '1 1 auto', minWidth: '120px', maxWidth: '100%' }}
                                    placeholder="Enter recipient email"
                                    value={replyRecipient ?? ""}
                                    onChange={(e) => setReplyRecipient(e.target.value)}
                                    onKeyPress={handleReplyRecipientKeyPress}
                                    onBlur={handleReplyRecipientChipCreation}
                                  />
                                </div>
                              </div>
                              {!showReplyCc && (
                                <button
                                  className="btn btn-link btn-sm"
                                  onClick={() => setShowReplyCc(true)}
                                  style={{ color: '#1a73e8', textDecoration: 'none', padding: '0 30px 0 0', fontSize: '14px', flexShrink: 0 }}
                                >
                                  Cc
                                </button>
                              )}
                            </div>
                            {showReplyCc && (
                              <div className="reply-cc mb-1 d-flex align-items-start">
                                <span className="text-muted fw-medium me-2" style={{ minWidth: '30px', paddingTop: '5px' }}>Cc:</span>
                                <div className="email-input-container d-flex flex-wrap align-items-center gap-1" style={{ flex: '1', width: '100%', maxWidth: 'calc(100% - 40px)' }}>
                                  {/* Reply CC chips */}
                                  {replyCcChips.map((chip, index) => (
                                    <span
                                      key={index}
                                      className={`email-chip ${chip.isValid ? 'valid' : 'invalid'}`}
                                    >
                                      {chip.email}
                                      <button
                                        className="chip-remove"
                                        onClick={() => removeReplyCcChip(index)}
                                        type="button"
                                      >
                                        ×
                                      </button>
                                    </span>
                                  ))}
                                  <input
                                    type="email"
                                    className="form-control form-control-sm chip-input"
                                    style={{ border: 'none', outline: 'none', boxShadow: 'none', flex: '1 1 auto', minWidth: '120px', maxWidth: '100%' }}
                                    placeholder="Enter CC email"
                                    value={replyCc ?? ""}
                                    onChange={(e) => setReplyCc(e.target.value)}
                                    onKeyPress={handleReplyCcKeyPress}
                                    onBlur={handleReplyCcChipCreation}
                                  />
                                </div>
                              </div>
                            )}
                            <div className="reply-subject">
                              <span className="text-muted fw-medium me-2">Subject:</span>
                              <span>{selectedEmail.subject.startsWith('Re:') ? selectedEmail.subject : `Re: ${selectedEmail.subject}`}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="reply-content">
                        <div
                          ref={replyMessageRef}
                          className="reply-editor border rounded p-3"
                          contentEditable
                          onInput={(e) => setReplyContent(e.currentTarget.innerHTML)}
                          onClick={() => saveReplyRange()}
                          onKeyUp={() => saveReplyRange()}
                          style={{
                            minHeight: '120px',
                            outline: 'none',
                            fontFamily: 'inherit',
                            fontSize: '14px',
                            lineHeight: '1.5'
                          }}
                          data-placeholder="Write your reply..."
                        />
                      </div>

                      {/* Reply Attachments */}
                      {replyAttachments.length > 0 && (
                        <div className="reply-attachments-list mt-2">
                          <div className="attachments-header">
                            <i className="fas fa-paperclip"></i>
                            <span>Attachments ({replyAttachments.length})</span>
                          </div>
                          {replyAttachments.map((file, index) => (
                            <div key={index} className="attachment-item">
                              <i className="fas fa-file"></i>
                              <span className="file-name">{file.name}</span>
                              <span className="file-size">({(file.size / 1024).toFixed(1)} KB)</span>
                              <button
                                className="btn btn-icon btn-sm"
                                onClick={() => handleRemoveReplyAttachment(index)}
                                title="Remove attachment"
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Hidden file input for reply */}
                      <input
                        type="file"
                        ref={replyFileInputRef}
                        onChange={handleReplyFileChange}
                        multiple
                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.bmp,.zip,.rar,.xls,.xlsx,.ppt,.pptx"
                        style={{ display: 'none' }}
                      />

                      <div className="reply-actions mt-3">
                        {/* Actions Row */}
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="reply-attachments d-flex align-items-center">
                            <button
                              className="btn btn-icon btn-sm me-2"
                              title="Attach files"
                              onClick={handleReplyFileClick}
                            >
                              <i className="fas fa-paperclip"></i>
                              {replyAttachments.length > 0 && (
                                <span className="attachment-count">{replyAttachments.length}</span>
                              )}
                            </button>
                            <button
                              className="btn btn-icon btn-sm me-2"
                              title="Insert link"
                              onClick={() => {
                                saveReplyRange();
                                setShowReplyLinkInput(true);
                              }}
                            >
                              <i className="fas fa-link"></i>
                            </button>
                            <div
                              className="position-relative d-inline-block me-2"
                              onMouseEnter={() => setShowReplyFormatDropdown(true)}
                              onMouseLeave={() => setShowReplyFormatDropdown(false)}
                            >
                              <button
                                className="btn btn-icon btn-sm"
                                title="Formatting options"
                              >
                                <i className="fas fa-font"></i>
                              </button>
                              {showReplyFormatDropdown && (
                                <div
                                  className="position-absolute bg-white border rounded shadow-sm p-2"
                                  style={{
                                    bottom: '100%',
                                    left: '0',
                                    marginBottom: '5px',
                                    zIndex: 1060,
                                    minWidth: '300px'
                                  }}
                                >
                                  <div className="format-row">
                                    <button
                                      className={`btn btn-icon btn-sm ${replyActiveFormats.has('strikethrough') ? 'active' : ''}`}
                                      title="Strikethrough"
                                      onClick={() => handleReplyFormatAction('strikethrough')}
                                    >
                                      <i className="fas fa-strikethrough"></i>
                                    </button>
                                    <button
                                      className={`btn btn-icon btn-sm ${replyActiveFormats.has('alignLeft') ? 'active' : ''}`}
                                      title="Align Left"
                                      onClick={() => handleReplyFormatAction('justifyLeft')}
                                    >
                                      <i className="fas fa-align-left"></i>
                                    </button>
                                    <button
                                      className={`btn btn-icon btn-sm ${replyActiveFormats.has('alignCenter') ? 'active' : ''}`}
                                      title="Align Center"
                                      onClick={() => handleReplyFormatAction('justifyCenter')}
                                    >
                                      <i className="fas fa-align-center"></i>
                                    </button>
                                    <button
                                      className={`btn btn-icon btn-sm ${replyActiveFormats.has('alignRight') ? 'active' : ''}`}
                                      title="Align Right"
                                      onClick={() => handleReplyFormatAction('justifyRight')}
                                    >
                                      <i className="fas fa-align-right"></i>
                                    </button>
                                    <button
                                      className={`btn btn-icon btn-sm ${replyActiveFormats.has('alignJustify') ? 'active' : ''}`}
                                      title="Justify"
                                      onClick={() => handleReplyFormatAction('justifyFull')}
                                    >
                                      <i className="fas fa-align-justify"></i>
                                    </button>
                                    <button
                                      className={`btn btn-icon btn-sm ${replyActiveFormats.has('unorderedList') ? 'active' : ''}`}
                                      title="Bullet List"
                                      onClick={() => handleReplyFormatAction('insertUnorderedList')}
                                    >
                                      <i className="fas fa-list-ul"></i>
                                    </button>
                                    <button
                                      className={`btn btn-icon btn-sm ${replyActiveFormats.has('orderedList') ? 'active' : ''}`}
                                      title="Numbered List"
                                      onClick={() => handleReplyFormatAction('insertOrderedList')}
                                    >
                                      <i className="fas fa-list-ol"></i>
                                    </button>
                                    <button
                                      className="btn btn-icon btn-sm"
                                      title="Indent"
                                      onClick={() => handleReplyFormatAction('indent')}
                                    >
                                      <i className="fas fa-indent"></i>
                                    </button>
                                    <button
                                      className="btn btn-icon btn-sm"
                                      title="Outdent"
                                      onClick={() => handleReplyFormatAction('outdent')}
                                    >
                                      <i className="fas fa-outdent"></i>
                                    </button>
                                    <button
                                      className="btn btn-icon btn-sm"
                                      title="Remove Formatting"
                                      onClick={() => handleReplyFormatAction('removeFormat')}
                                    >
                                      <i className="fas fa-eraser"></i>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="position-relative d-inline-block me-2">
                              <button
                                className="btn btn-icon btn-sm"
                                title="Insert emoji"
                                onClick={() => {
                                  saveReplyRange();
                                  setShowReplyEmojiPicker(!showReplyEmojiPicker);
                                }}
                              >
                                <i className="far fa-smile"></i>
                              </button>
                              {showReplyEmojiPicker && (
                                <div className="position-absolute" style={{ zIndex: 1050, bottom: '100%', left: 0 }}>
                                  <EmojiPicker onEmojiClick={handleReplyEmojiClick} />
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="reply-send">
                            <button
                              className="btn btn-secondary btn-sm me-2"
                              onClick={handleCancelReply}
                            >
                              Cancel
                            </button>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={handleSendReply}
                              disabled={sendingReply || !replyContent.trim()}
                            >
                              {sendingReply ? (
                                <>
                                  <i className="fas fa-spinner fa-spin me-2"></i>
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-paper-plane me-2"></i>
                                  Send
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Gmail-style Forward Interface */}
                  {isForwardMode && selectedEmail && (
                    <div className="email-forward-container mt-4 pt-4 border-top">
                      <div className="forward-header mb-3">
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="forward-info w-100">
                            <div className="forward-to mb-2 d-flex align-items-start justify-content-between">
                              <div className="d-flex align-items-start" style={{ flex: '1', width: '100%' }}>
                                <span className="text-muted fw-medium me-2" style={{ minWidth: '30px', paddingTop: '5px' }}>To:</span>
                                <div className="email-input-container d-flex flex-wrap align-items-center gap-1" style={{ flex: '1', width: '100%', maxWidth: 'calc(100% - 70px)' }}>
                                  {/* Forward recipient chips */}
                                  {forwardRecipientChips.map((chip, index) => (
                                    <span
                                      key={index}
                                      className={`email-chip ${chip.isValid ? 'valid' : 'invalid'}`}
                                    >
                                      {chip.email}
                                      <button
                                        className="chip-remove"
                                        onClick={() => removeForwardRecipientChip(index)}
                                        type="button"
                                      >
                                        ×
                                      </button>
                                    </span>
                                  ))}
                                  <input
                                    type="email"
                                    className="form-control form-control-sm chip-input"
                                    style={{ border: 'none', outline: 'none', boxShadow: 'none', flex: '1 1 auto', minWidth: '120px', maxWidth: '100%' }}
                                    placeholder="Enter recipient email"
                                    value={forwardRecipient ?? ""}
                                    onChange={(e) => setForwardRecipient(e.target.value)}
                                    onKeyPress={handleForwardRecipientKeyPress}
                                    onBlur={handleForwardRecipientChipCreation}
                                  />
                                </div>
                              </div>
                              {!showForwardCc && (
                                <button
                                  className="btn btn-link btn-sm"
                                  onClick={() => setShowForwardCc(true)}
                                  style={{ color: '#1a73e8', textDecoration: 'none', padding: '5px 30px 0 0', fontSize: '14px', flexShrink: 0 }}
                                >
                                  Cc
                                </button>
                              )}
                            </div>
                            {showForwardCc && (
                              <div className="forward-cc mb-2 d-flex align-items-start">
                                <span className="text-muted fw-medium me-2" style={{ minWidth: '30px', paddingTop: '5px' }}>Cc:</span>
                                <div className="email-input-container d-flex flex-wrap align-items-center gap-1" style={{ flex: '1' }}>
                                  {/* Forward CC chips */}
                                  {forwardCcChips.map((chip, index) => (
                                    <span
                                      key={index}
                                      className={`email-chip ${chip.isValid ? 'valid' : 'invalid'}`}
                                    >
                                      {chip.email}
                                      <button
                                        className="chip-remove"
                                        onClick={() => removeForwardCcChip(index)}
                                        type="button"
                                      >
                                        ×
                                      </button>
                                    </span>
                                  ))}
                                  <input
                                    type="email"
                                    className="form-control form-control-sm chip-input"
                                    style={{ border: 'none', outline: 'none', boxShadow: 'none', flex: '1', minWidth: '120px' }}
                                    placeholder="Enter CC email"
                                    value={forwardCc ?? ""}
                                    onChange={(e) => setForwardCc(e.target.value)}
                                    onKeyPress={handleForwardCcKeyPress}
                                    onBlur={handleForwardCcChipCreation}
                                  />
                                </div>
                              </div>
                            )}
                            <div className="forward-subject d-flex align-items-center justify-content-between w-100">
                              <div className="d-flex align-items-center">
                                <span className="text-muted fw-medium me-2">Subject:</span>
                                <span>{selectedEmail.subject.startsWith('Fwd:') ? selectedEmail.subject : `Fwd: ${selectedEmail.subject}`}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="forward-content">
                        {/* Forward Message Editor */}
                        <div className="forward-message-editor mb-3">
                          {/* Message Editor */}
                          <div
                            ref={forwardMessageRef}
                            className="form-control"
                            contentEditable
                            suppressContentEditableWarning
                            onInput={(e) => setOptionalMessage(e.currentTarget.innerHTML)}
                            onClick={() => saveForwardRange()}
                            onKeyUp={() => saveForwardRange()}
                            style={{
                              minHeight: '120px',
                              border: '1px solid #e9ecef',
                              borderRadius: '8px',
                              fontSize: '14px',
                              lineHeight: '1.5',
                              padding: '12px',
                              fontFamily: 'inherit',
                              outline: 'none',
                              overflowY: 'auto'
                            }}
                          />
                        </div>

                        {/* Gmail-style Forwarded Email Dropdown */}
                        <div className="forwarded-email-dropdown mb-3">
                          <div
                            className="dropdown-header"
                            onClick={() => setIsForwardDropdownOpen(!isForwardDropdownOpen)}
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #e9ecef',
                              borderBottom: isForwardDropdownOpen ? 'none' : '1px solid #e9ecef',
                              padding: '12px 16px',
                              borderRadius: isForwardDropdownOpen ? '8px 8px 0 0' : '8px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              transition: 'all 0.2s ease',
                              userSelect: 'none'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f1f3f4';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                            }}
                          >
                            <div className="dropdown-title" style={{
                              fontSize: '14px',
                              fontWeight: '500',
                              color: '#5f6368'
                            }}>
                              <i
                                className={`fas ${isForwardDropdownOpen ? 'fa-chevron-down' : 'fa-chevron-right'} me-2`}
                                style={{ fontSize: '12px', transition: 'transform 0.2s ease' }}
                              ></i>
                              ---------- Forwarded message ---------
                            </div>
                          </div>

                          {isForwardDropdownOpen && (
                            <div className="dropdown-content" style={{
                              backgroundColor: '#ffffff',
                              border: '1px solid #e9ecef',
                              borderTop: 'none',
                              borderRadius: '0 0 8px 8px',
                              padding: '16px',
                              animation: 'slideDown 0.2s ease'
                            }}>
                              <div className="forwarded-email-details">
                                <div className="email-detail-item mb-2" style={{ fontSize: '13px' }}>
                                  <div className="d-flex">
                                    <div className="text-muted fw-medium" style={{ minWidth: '60px', flexShrink: 0, color: '#5f6368' }}>From:</div>
                                    <div className="flex-grow-1" style={{ color: '#202124' }}>
                                      {extractSenderInfo(selectedEmail.send_from).name} &lt;{extractSenderInfo(selectedEmail.send_from).email}&gt;
                                    </div>
                                  </div>
                                </div>
                                <div className="email-detail-item mb-2" style={{ fontSize: '13px' }}>
                                  <div className="d-flex">
                                    <div className="text-muted fw-medium" style={{ minWidth: '60px', flexShrink: 0, color: '#5f6368' }}>To:</div>
                                    <div className="flex-grow-1" style={{ color: '#202124' }}>{selectedEmail.send_to}</div>
                                  </div>
                                </div>
                                {selectedEmail.mail_cc && (
                                  <div className="email-detail-item mb-2" style={{ fontSize: '13px' }}>
                                    <div className="d-flex">
                                      <div className="text-muted fw-medium" style={{ minWidth: '60px', flexShrink: 0, color: '#5f6368' }}>CC:</div>
                                      <div className="flex-grow-1" style={{ color: '#202124' }}>{selectedEmail.mail_cc}</div>
                                    </div>
                                  </div>
                                )}
                                <div className="email-detail-item mb-2" style={{ fontSize: '13px' }}>
                                  <div className="d-flex">
                                    <div className="text-muted fw-medium" style={{ minWidth: '60px', flexShrink: 0, color: '#5f6368' }}>Date:</div>
                                    <div className="flex-grow-1" style={{ color: '#202124' }}>{formatFullEmailDate(selectedEmail.email_datetime)}</div>
                                  </div>
                                </div>
                                <div className="email-detail-item mb-3" style={{ fontSize: '13px' }}>
                                  <div className="d-flex">
                                    <div className="text-muted fw-medium" style={{ minWidth: '60px', flexShrink: 0, color: '#5f6368' }}>Subject:</div>
                                    <div className="flex-grow-1" style={{ color: '#202124' }}>{selectedEmail.subject}</div>
                                  </div>
                                </div>
                                {/* Original email content with Gmail styling */}
                                <div className="forwarded-email-body" style={{
                                  borderTop: '1px solid #e9ecef',
                                  paddingTop: '12px',
                                  fontSize: '13px',
                                  lineHeight: '1.5',
                                  color: '#202124'
                                }}>
                                  <div
                                    className="scoped-email-content forwarded-email-content"
                                    dangerouslySetInnerHTML={{ __html: processedEmailContent?.html || selectedEmail.content }}
                                    style={{
                                      fontSize: '13px',
                                      lineHeight: '1.5',
                                      color: '#202124'
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Forward Attachments */}
                      {forwardAttachments.length > 0 && (
                        <div className="forward-attachments-list mt-2">
                          <div className="attachments-header">
                            <i className="fas fa-paperclip"></i>
                            <span>Attachments ({forwardAttachments.length})</span>
                          </div>
                          {forwardAttachments.map((file, index) => (
                            <div key={index} className="attachment-item">
                              <i className="fas fa-file"></i>
                              <span className="file-name">{file.name}</span>
                              <span className="file-size">({(file.size / 1024).toFixed(1)} KB)</span>
                              <button
                                className="btn btn-icon btn-sm"
                                onClick={() => handleRemoveForwardAttachment(index)}
                                title="Remove attachment"
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Hidden file input for forward */}
                      <input
                        type="file"
                        ref={forwardFileInputRef}
                        onChange={handleForwardFileChange}
                        multiple
                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.bmp,.zip,.rar,.xls,.xlsx,.ppt,.pptx"
                        style={{ display: 'none' }}
                      />

                      <div className="forward-actions mt-3">
                        {/* Actions Row */}
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="forward-attachments d-flex align-items-center">
                            <button className="btn btn-icon btn-sm me-2" title="Attach files" onClick={handleForwardFileClick}>
                              <i className="fas fa-paperclip"></i>
                              {forwardAttachments.length > 0 && (
                                <span className="attachment-count">{forwardAttachments.length}</span>
                              )}
                            </button>
                            <button
                              className="btn btn-icon btn-sm me-2"
                              title="Insert link"
                              onClick={() => {
                                saveForwardRange();
                                setShowForwardLinkInput(true);
                              }}
                            >
                              <i className="fas fa-link"></i>
                            </button>
                            <div
                              className="position-relative d-inline-block me-2"
                              onMouseEnter={() => setShowForwardFormatDropdown(true)}
                              onMouseLeave={() => setShowForwardFormatDropdown(false)}
                            >
                              <button
                                className="btn btn-icon btn-sm"
                                title="Formatting options"
                              >
                                <i className="fas fa-font"></i>
                              </button>
                              {showForwardFormatDropdown && (
                                <div
                                  className="position-absolute bg-white border rounded shadow-sm p-2"
                                  style={{
                                    bottom: '100%',
                                    left: '0',
                                    marginBottom: '5px',
                                    zIndex: 1060,
                                    minWidth: '300px'
                                  }}
                                >
                                  <div className="format-row">
                                    <button
                                      className={`btn btn-icon btn-sm ${forwardActiveFormats.has('strikethrough') ? 'active' : ''}`}
                                      title="Strikethrough"
                                      onClick={() => handleForwardFormatAction('strikethrough')}
                                    >
                                      <i className="fas fa-strikethrough"></i>
                                    </button>
                                    <button
                                      className={`btn btn-icon btn-sm ${forwardActiveFormats.has('alignLeft') ? 'active' : ''}`}
                                      title="Align Left"
                                      onClick={() => handleForwardFormatAction('justifyLeft')}
                                    >
                                      <i className="fas fa-align-left"></i>
                                    </button>
                                    <button
                                      className={`btn btn-icon btn-sm ${forwardActiveFormats.has('alignCenter') ? 'active' : ''}`}
                                      title="Align Center"
                                      onClick={() => handleForwardFormatAction('justifyCenter')}
                                    >
                                      <i className="fas fa-align-center"></i>
                                    </button>
                                    <button
                                      className={`btn btn-icon btn-sm ${forwardActiveFormats.has('alignRight') ? 'active' : ''}`}
                                      title="Align Right"
                                      onClick={() => handleForwardFormatAction('justifyRight')}
                                    >
                                      <i className="fas fa-align-right"></i>
                                    </button>
                                    <button
                                      className={`btn btn-icon btn-sm ${forwardActiveFormats.has('alignJustify') ? 'active' : ''}`}
                                      title="Justify"
                                      onClick={() => handleForwardFormatAction('justifyFull')}
                                    >
                                      <i className="fas fa-align-justify"></i>
                                    </button>
                                    <button
                                      className={`btn btn-icon btn-sm ${forwardActiveFormats.has('unorderedList') ? 'active' : ''}`}
                                      title="Bullet List"
                                      onClick={() => handleForwardFormatAction('insertUnorderedList')}
                                    >
                                      <i className="fas fa-list-ul"></i>
                                    </button>
                                    <button
                                      className={`btn btn-icon btn-sm ${forwardActiveFormats.has('orderedList') ? 'active' : ''}`}
                                      title="Numbered List"
                                      onClick={() => handleForwardFormatAction('insertOrderedList')}
                                    >
                                      <i className="fas fa-list-ol"></i>
                                    </button>
                                    <button
                                      className="btn btn-icon btn-sm"
                                      title="Indent"
                                      onClick={() => handleForwardFormatAction('indent')}
                                    >
                                      <i className="fas fa-indent"></i>
                                    </button>
                                    <button
                                      className="btn btn-icon btn-sm"
                                      title="Outdent"
                                      onClick={() => handleForwardFormatAction('outdent')}
                                    >
                                      <i className="fas fa-outdent"></i>
                                    </button>
                                    <button
                                      className="btn btn-icon btn-sm"
                                      title="Remove Formatting"
                                      onClick={() => handleForwardFormatAction('removeFormat')}
                                    >
                                      <i className="fas fa-eraser"></i>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="position-relative d-inline-block me-2">
                              <button
                                className="btn btn-icon btn-sm"
                                title="Insert emoji"
                                onClick={() => {
                                  saveForwardRange();
                                  setShowForwardEmojiPicker(!showForwardEmojiPicker);
                                }}
                              >
                                <i className="far fa-smile"></i>
                              </button>
                              {showForwardEmojiPicker && (
                                <div className="position-absolute" style={{ zIndex: 1050, bottom: '100%', left: 0 }}>
                                  <EmojiPicker onEmojiClick={handleForwardEmojiClick} />
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="d-flex align-items-center">
                            {optionalMessage.trim() && (
                              <button className="btn btn-link btn-sm me-3" title="Clear message" onClick={() => setOptionalMessage('')} style={{ color: '#5f6368', fontSize: '12px' }}>Clear message</button>
                            )}
                            <div className="forward-send">
                              <button className="btn btn-secondary btn-sm me-2" onClick={handleCancelForward}>Cancel</button>
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={handleSendForward}
                                disabled={sendingForward || (getAllEmails(forwardRecipientChips, forwardRecipient).length === 0)}
                              >
                                {sendingForward ? (
                                  <>
                                    <i className="fas fa-spinner fa-spin me-2"></i>
                                    Sending...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-paper-plane me-2"></i>
                                    Send
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="email-content" style={{ minHeight: '60vh', maxHeight: '70vh', overflow: 'auto' }}>
              <div className="table-responsive">
                <table className="table table-inbox table-hover">
                  <tbody>
                    {emailResponse.emails.map((email: any) => {
                      if (!email || !email.mail_message_id) {
                        return null;
                      }

                      const senderInfo = extractSenderInfo(email.send_from || "");
                      const emailId = email.mail_message_id.toString();
                      const isUnread = !email.is_read;
                      const providerTag = getEmailProviderTag(email.mail_provider_key || "");

                      return (
                        <tr
                          key={emailId}
                          className={`${isUnread ? "unread" : ""} clickable-row`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleEmailClick(emailId)}
                        >
                          <td
                            style={{ width: "40px" }}
                            onClick={(e) => handleCheckboxClick(e, emailId)}
                          >
                            <div className="form-check form-check-md">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={selectedEmails.has(emailId)}
                                onChange={() => { }}
                                onClick={(e) => handleCheckboxClick(e, emailId)}
                              />
                            </div>
                          </td>
                          <td
                            style={{ width: "40px" }}
                            onClick={(e) => handleStarClickWithAPI(e, emailId, email.is_important)}
                          >
                            <span className="mail-important">
                              <i
                                className={`fas fa-star ${email.is_important ? "starred" : ""
                                  }`}
                                style={{ cursor: "pointer" }}
                              />
                            </span>
                          </td>
                          <td
                            className="name"
                            style={{
                              width: "180px",
                              maxWidth: "180px",
                            }}
                          >
                            <div
                              className="text-truncate"
                              title={senderInfo.name}
                            >
                              {senderInfo.name}
                            </div>
                          </td>
                          <td
                            className="subject"
                            style={{ minWidth: "0", flex: 1 }}
                          >
                            <div
                              className="text-truncate"
                              title={email.subject}
                            >
                              {truncateText(email.subject, 50)}
                            </div>
                          </td>
                          <td style={{ width: "40px" }}>
                            {email.attachments && email.attachments.length > 0 && (
                              <i className="fas fa-paperclip" />
                            )}
                          </td>
                          <td
                            className="mail-date"
                            style={{ width: "120px" }}
                          >
                            <div className="d-flex align-items-center justify-content-between">
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: "#6c757d",
                                  color: "white",
                                  fontSize: "0.7rem",
                                  padding: "0.2rem 0.4rem",
                                  borderRadius: "4px",
                                  fontWeight: "500",
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                  marginRight: "8px",
                                }}
                              >
                                {email.mail_message_type_id?.mail_message_type_key || "INBOX"}
                              </span>
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: providerTag.bgColor,
                                  color: providerTag.color,
                                  fontSize: "0.7rem",
                                  padding: "0.2rem 0.4rem",
                                  borderRadius: "4px",
                                  fontWeight: "500",
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                  marginRight: "8px",
                                }}
                              >
                                <i
                                  className={providerTag.icon}
                                  style={{
                                    marginRight: "0.2rem",
                                    fontSize: "0.8rem",
                                  }}
                                ></i>
                                {providerTag.label}
                              </span>
                              <span
                                className="text-muted small"
                                style={{ minWidth: "fit-content" }}
                              >
                                {formatDate(email.email_datetime)}
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CSS Styles for Email Components */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .email-chip {
            display: inline-flex;
            align-items: center;
            background-color: #e3f2fd;
            border: 1px solid #2196f3;
            border-radius: 16px;
            padding: 4px 8px;
            margin: 2px;
            font-size: 12px;
            color: #1976d2;
            flex-shrink: 0;
          }
          
          .email-chip.valid {
            background-color: #e8f5e8;
            border-color: #4caf50;
            color: #2e7d32;
          }
          
          .email-chip.invalid {
            background-color: #ffebee;
            border-color: #f44336;
            color: #c62828;
          }
          
          .chip-remove {
            background: none;
            border: none;
            color: inherit;
            font-size: 14px;
            margin-left: 4px;
            cursor: pointer;
            padding: 0;
            line-height: 1;
          }
          
          .chip-remove:hover {
            opacity: 0.7;
          }
          
          .chip-input {
            background: transparent;
            border: none;
            outline: none;
            font-size: 12px;
            min-width: 120px;
          }
          
          .attachment-item {
            display: flex;
            align-items: center;
            padding: 8px;
            border: 1px solid #e9ecef;
            border-radius: 4px;
            margin: 4px 0;
            background-color: #f8f9fa;
          }
          
          .attachment-item i {
            margin-right: 8px;
            color: #6c757d;
          }
          
          .file-name {
            flex: 1;
            font-size: 12px;
            color: #495057;
          }
          
          .file-size {
            font-size: 11px;
            color: #6c757d;
            margin: 0 8px;
          }
          
          .attachments-header {
            display: flex;
            align-items: center;
            font-size: 12px;
            color: #6c757d;
            margin-bottom: 8px;
          }
          
          .attachments-header i {
            margin-right: 4px;
          }
          
          .attachment-count {
            background-color: #007bff;
            color: white;
            border-radius: 50%;
            width: 16px;
            height: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            margin-left: 4px;
          }
          
          .gmail-attachment-header {
            margin-bottom: 16px;
          }
          
          .gmail-attachment-header-content {
            display: flex;
            align-items: center;
            font-size: 14px;
            color: #5f6368;
          }
          
          .gmail-attachment-icon {
            margin-right: 8px;
            color: #5f6368;
          }
          
          .gmail-attachments-container {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }
          
          .gmail-attachment-item {
            border: 1px solid #e9ecef;
            border-radius: 8px;
            padding: 12px;
            background-color: #ffffff;
          }
          
          .gmail-attachment-content {
            display: flex;
            align-items: center;
            gap: 12px;
          }
          
          .gmail-attachment-icon {
            flex-shrink: 0;
          }
          
          .gmail-file-icon {
            width: 40px;
            height: 40px;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 16px;
          }
          
          .gmail-attachment-info {
            flex: 1;
            min-width: 0;
          }
          
          .gmail-file-name {
            font-size: 14px;
            font-weight: 500;
            color: #202124;
            margin-bottom: 2px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          
          .gmail-file-type {
            font-size: 12px;
            color: #5f6368;
          }
          
          .gmail-attachment-actions {
            display: flex;
            gap: 8px;
          }
          
          .gmail-download-btn,
          .gmail-open-btn {
            background: none;
            border: none;
            color: #5f6368;
            cursor: pointer;
            padding: 4px;
            border-radius: 4px;
            transition: background-color 0.2s;
          }
          
          .gmail-download-btn:hover,
          .gmail-open-btn:hover {
            background-color: #f1f3f4;
            color: #202124;
          }
          
          .unread {
            background-color: #f8f9fa;
            font-weight: 500;
          }
          
          .clickable-row:hover {
            background-color: #f8f9fa;
          }
          
          .starred {
            color: #ffc107;
          }
          
          .mail-important .fas.fa-star {
            color: #6c757d;
            cursor: pointer;
            transition: color 0.2s;
          }
          
          .mail-important .fas.fa-star:hover {
            color: #ffc107;
          }
          
          .mail-important .fas.fa-star.starred {
            color: #ffc107;
          }
          
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `
      }} />

      {/* Reply Link Insert Modal */}
      {showReplyLinkInput && (
        <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }}>
          <div className="modal-dialog" style={{ position: 'relative', margin: '1.75rem auto', maxWidth: '500px' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Insert Link</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowReplyLinkInput(false);
                    setReplyLinkUrl('');
                    setReplyLinkText('');
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="replyLinkText" className="form-label">Text to display</label>
                  <input
                    type="text"
                    id="replyLinkText"
                    className="form-control"
                    placeholder="Link text"
                    value={replyLinkText ?? ""}
                    onChange={(e) => setReplyLinkText(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="replyLinkUrl" className="form-label">URL</label>
                  <input
                    type="text"
                    id="replyLinkUrl"
                    className="form-control"
                    placeholder="https://"
                    value={replyLinkUrl ?? ""}
                    onChange={(e) => setReplyLinkUrl(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowReplyLinkInput(false);
                    setReplyLinkUrl('');
                    setReplyLinkText('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleReplyInsertLink}
                  disabled={!replyLinkUrl}
                >
                  Insert Link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Forward Link Insert Modal */}
      {showForwardLinkInput && (
        <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }}>
          <div className="modal-dialog" style={{ position: 'relative', margin: '1.75rem auto', maxWidth: '500px' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Insert Link</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowForwardLinkInput(false);
                    setForwardLinkUrl('');
                    setForwardLinkText('');
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="forwardLinkText" className="form-label">Text to display</label>
                  <input
                    type="text"
                    id="forwardLinkText"
                    className="form-control"
                    placeholder="Link text"
                    value={forwardLinkText ?? ""}
                    onChange={(e) => setForwardLinkText(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="forwardLinkUrl" className="form-label">URL</label>
                  <input
                    type="text"
                    id="forwardLinkUrl"
                    className="form-control"
                    placeholder="https://"
                    value={forwardLinkUrl ?? ""}
                    onChange={(e) => setForwardLinkUrl(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowForwardLinkInput(false);
                    setForwardLinkUrl('');
                    setForwardLinkText('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleForwardInsertLink}
                  disabled={!forwardLinkUrl}
                >
                  Insert Link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Email;
